import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Send as SendIcon,
  People as PeopleIcon,
  Email as EmailIcon,
  Preview as PreviewIcon,
  MarkEmailRead as MarkEmailReadIcon,
  Group as GroupIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Code as CodeIcon,
  TextFields as TextFieldsIcon,
  UploadFile as UploadFileIcon,
  Save as SaveIcon,
  Schedule as ScheduleIcon,
  Drafts as DraftsIcon,
  WavingHand as WavingHandIcon,
} from '@mui/icons-material';
import { Select, MenuItem, FormControl, IconButton, Tooltip, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { adminApi } from '../services/adminApi';

const NEWSLETTER_API = `${process.env.REACT_APP_EXPRESS_API_URL || 'https://famelia-admin-api-web.azurewebsites.net'}/api/admin/newsletter`;

interface Subscriber { id: string; name: string; email: string; }
interface SendResult { sent: number; failed: number; errors?: string[]; recipients?: string[]; }
interface Campaign { id: string; subject: string; sent_at: string; recipients_count: number; opened_count: number; clicked_count: number; }
interface NewsletterGroup { id: string; name: string; description: string; exclude_patterns: string[]; include_only_emails: string[]; created_at: string; }
interface Draft { id: string; subject: string; sender_name: string; html_body?: string; group_id?: string; status: string; scheduled_at: string | null; sent_at: string | null; created_at: string; updated_at: string; }
interface WelcomeTemplate { id: string; subject: string; html_body: string; sender_name: string; is_active: boolean; created_at: string; updated_at: string; }

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    [{ font: [] }],
    [{ size: ['small', false, 'large', 'huge'] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    ['blockquote'],
    ['link', 'image'],
    ['clean'],
  ],
  clipboard: { matchVisual: false },
};

const QUILL_FORMATS = [
  'header', 'font', 'size', 'bold', 'italic', 'underline', 'strike',
  'color', 'background', 'align', 'list', 'indent', 'blockquote', 'link', 'image',
];

function EmailEditor({ value, onChange, editorMode, setEditorMode }: {
  value: string; onChange: (v: string) => void; editorMode: 'visual' | 'code'; setEditorMode: (m: 'visual' | 'code') => void;
}) {
  const [warnOpen, setWarnOpen] = useState(false);

  const handleModeChange = (_: any, newMode: 'visual' | 'code' | null) => {
    if (!newMode) return;
    if (newMode === 'visual' && editorMode === 'code') {
      // Switching to visual will strip complex HTML — warn the user
      const isComplex = /<style[\s>]|<!DOCTYPE|<table[\s>]|class="[^"]*ers-/i.test(value);
      if (isComplex) { setWarnOpen(true); return; }
    }
    setEditorMode(newMode);
  };

  return (
    <Box>
      {/* Warning dialog for switching to visual with complex HTML */}
      <Dialog open={warnOpen} onClose={() => setWarnOpen(false)} maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Switch to Visual Editor?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This email contains complex HTML (from Canva or another builder). Switching to the Visual editor will <strong>strip background colors, custom fonts, and layout formatting</strong> that the visual editor doesn't support.
          </Typography>
          <Typography variant="body2" mt={1.5} color="text.secondary">
            Use <strong>HTML Code</strong> mode to safely edit this email without losing formatting.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setWarnOpen(false)} sx={{ textTransform: 'none' }}>Stay in Code Mode</Button>
          <Button variant="contained" color="warning" onClick={() => { setWarnOpen(false); setEditorMode('visual'); }}
            sx={{ textTransform: 'none', borderRadius: '16px' }}>
            Switch Anyway (loses formatting)
          </Button>
        </DialogActions>
      </Dialog>

      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="subtitle2" fontWeight={700} color="text.secondary">Body</Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title="Upload HTML or images">
            <Button size="small" startIcon={<UploadFileIcon />} component="label"
              sx={{ textTransform: 'none', color: '#555', fontWeight: 600, fontSize: '0.8rem' }}>
              Upload Files
              <input type="file" accept=".html,.htm,image/*" multiple hidden onChange={(e) => {
                const files = Array.from(e.target.files || []);
                for (const file of files) {
                  if (file.name.endsWith('.html') || file.name.endsWith('.htm')) {
                    const reader = new FileReader();
                    reader.onload = (ev) => { const c = ev.target?.result as string; if (c) { onChange(c); setEditorMode('code'); } };
                    reader.readAsText(file);
                  } else if (file.type.startsWith('image/')) {
                    const reader = new FileReader();
                    reader.onload = (ev) => { const d = ev.target?.result as string; if (d) onChange(value + `<img src="${d}" alt="${file.name}" style="max-width:100%;height:auto;" />`); };
                    reader.readAsDataURL(file);
                  }
                }
                e.target.value = '';
              }} />
            </Button>
          </Tooltip>
          <ToggleButtonGroup value={editorMode} exclusive onChange={handleModeChange} size="small" sx={{ height: 32 }}>
            <ToggleButton value="visual" sx={{ textTransform: 'none', px: 1.5, fontSize: '0.8rem' }}>
              <TextFieldsIcon sx={{ fontSize: 16, mr: 0.5 }} /> Visual
            </ToggleButton>
            <ToggleButton value="code" sx={{ textTransform: 'none', px: 1.5, fontSize: '0.8rem' }}>
              <CodeIcon sx={{ fontSize: 16, mr: 0.5 }} /> HTML Code
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>
      <Typography variant="caption" color="text.secondary" mb={1} display="block">
        {editorMode === 'visual'
          ? <>Use the toolbar for fonts, formatting, colors, images, and links. Type {'{{firstName}}'} to personalize.</>
          : <>Edit raw HTML directly. Paste from Canva, Stripo, Beefree, etc. Use {'{{firstName}}'} for personalization.</>}
      </Typography>
      {editorMode === 'visual' ? (
        <Box sx={{ '.ql-toolbar': { borderRadius: '8px 8px 0 0', borderColor: '#E0E0E0', bgcolor: '#FAFAFA' }, '.ql-container': { borderRadius: '0 0 8px 8px', borderColor: '#E0E0E0', minHeight: 300, fontSize: '15px', fontFamily: 'inherit' }, '.ql-editor': { minHeight: 280, lineHeight: 1.6 }, '.ql-editor img': { maxWidth: '100%', height: 'auto', borderRadius: '8px' } }}>
          <ReactQuill theme="snow" value={value} onChange={onChange} modules={QUILL_MODULES} formats={QUILL_FORMATS} placeholder="Start designing your newsletter..." />
        </Box>
      ) : (
        <TextField fullWidth multiline minRows={16} maxRows={40} value={value} onChange={e => onChange(e.target.value)} variant="outlined"
          placeholder={'<!DOCTYPE html>\n<html>\n<body>\n  <h2>Hi {{firstName}},</h2>\n  <p>Here\'s what\'s new...</p>\n</body>\n</html>'}
          sx={{ '& .MuiOutlinedInput-root': { fontFamily: '"Fira Code", "SF Mono", monospace', fontSize: '13px', lineHeight: 1.5, bgcolor: '#FAFAFA', color: '#1E1E1E', borderRadius: 2 }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E0E0E0' }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#FF8C00' } }} />
      )}
    </Box>
  );
}

export function NewsletterTab() {
  const [innerTab, setInnerTab] = useState(0);
  const [editingDraft, setEditingDraft] = useState<Draft | null>(null);
  const authHeader = useMemo(() => ({ Authorization: adminApi.getAuthToken() }), []);

  const handleEditDraft = (draft: Draft) => {
    setEditingDraft(draft);
    setInnerTab(0);
  };

  const handleClearEdit = () => setEditingDraft(null);

  return (
    <Box>
      <Tabs value={innerTab} onChange={(_, v) => setInnerTab(v)} sx={{ mb: 3, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}>
        <Tab icon={<SendIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Compose & Send" />
        <Tab icon={<DraftsIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Saved Drafts" />
        <Tab icon={<WavingHandIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Welcome Email" />
      </Tabs>
      {innerTab === 0 && <ComposeSection authHeader={authHeader} editingDraft={editingDraft} onClearEdit={handleClearEdit} />}
      {innerTab === 1 && <DraftsSection authHeader={authHeader} onEditDraft={handleEditDraft} />}
      {innerTab === 2 && <WelcomeEmailSection authHeader={authHeader} />}
    </Box>
  );
}

// ─── COMPOSE & SEND SECTION ──────────────────────────────────────────────────
function ComposeSection({ authHeader, editingDraft, onClearEdit }: {
  authHeader: { Authorization: string };
  editingDraft: Draft | null;
  onClearEdit: () => void;
}) {
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [loadingSubscribers, setLoadingSubscribers] = useState(true);
  const [subscriberError, setSubscriberError] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<SendResult | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [groups, setGroups] = useState<NewsletterGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [groupPreviewCount, setGroupPreviewCount] = useState<number | null>(null);
  const [showGroupManager, setShowGroupManager] = useState(false);
  const [editingGroup, setEditingGroup] = useState<NewsletterGroup | null>(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');
  const [newGroupExcludes, setNewGroupExcludes] = useState('');
  const [newGroupIncludes, setNewGroupIncludes] = useState('');
  const [savingGroup, setSavingGroup] = useState(false);
  const [editorMode, setEditorMode] = useState<'visual' | 'code'>('visual');
  const [senderName, setSenderName] = useState('Famēlia');
  const [showPreviewSendDialog, setShowPreviewSendDialog] = useState(false);
  const [previewExtraEmails, setPreviewExtraEmails] = useState('');
  const [sendingPreview, setSendingPreview] = useState(false);
  const [previewResult, setPreviewResult] = useState<SendResult | null>(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');
  const [editingDraftId, setEditingDraftId] = useState<string | null>(null);

  // Preview state
  const [showPreview, setShowPreview] = useState(false);
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [editorCollapsed, setEditorCollapsed] = useState(false);

  // Send confirmation dialog
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [sendDialogGroupId, setSendDialogGroupId] = useState('');
  const [sendDialogSchedule, setSendDialogSchedule] = useState('');

  // Populate fields when editing a saved draft
  useEffect(() => {
    if (!editingDraft) return;
    setEditingDraftId(editingDraft.id);
    setSubject(editingDraft.subject ?? '');
    setSenderName(editingDraft.sender_name ?? 'Famēlia');
    setSelectedGroupId(editingDraft.group_id ?? '');
    setScheduledAt(editingDraft.scheduled_at ? new Date(editingDraft.scheduled_at).toISOString().slice(0, 16) : '');
    setDraftSaved(false);
    setSendResult(null);
    setSendError(null);
    let body = editingDraft.html_body ?? '';
    body = body.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
    body = body.replace(/\(function\(\)\{function c\(\)[\s\S]*?\}\)\(\);?/g, '');
    setHtmlBody(body);
    setEditorMode('code');
  }, [editingDraft]);

  const handleClearEditingDraft = () => {
    setEditingDraftId(null);
    setSubject('');
    setHtmlBody('');
    setSenderName('Famēlia');
    setSelectedGroupId('');
    setScheduledAt('');
    setDraftSaved(false);
    onClearEdit();
  };

  const fetchSubscribers = useCallback(async () => {
    setLoadingSubscribers(true); setSubscriberError(null);
    try { const r = await axios.get(`${NEWSLETTER_API}/subscribers`, { headers: authHeader }); setSubscribers(r.data.subscribers ?? []); }
    catch (err: any) { setSubscriberError(err.response?.data?.error ?? err.message ?? 'Failed to load subscribers.'); }
    finally { setLoadingSubscribers(false); }
  }, [authHeader]);

  const fetchCampaigns = useCallback(async () => {
    setLoadingCampaigns(true);
    try { const r = await axios.get(`${NEWSLETTER_API}/campaigns`, { headers: authHeader }); setCampaigns(r.data.campaigns ?? []); }
    catch { /* non-fatal */ } finally { setLoadingCampaigns(false); }
  }, [authHeader]);

  const fetchGroups = useCallback(async () => {
    try {
      const r = await axios.get(`${NEWSLETTER_API}/groups`, { headers: authHeader });
      setGroups(r.data.groups ?? []);
      // Never auto-select a group — always default to "all subscribers" (empty string)
    }
    catch { /* non-fatal */ }
  }, [authHeader]);

  const fetchGroupPreview = useCallback(async (gid: string) => {
    if (!gid) { setGroupPreviewCount(null); return; }
    try { const r = await axios.get(`${NEWSLETTER_API}/groups/${gid}/preview`, { headers: authHeader }); setGroupPreviewCount(r.data.count ?? null); }
    catch { setGroupPreviewCount(null); }
  }, [authHeader]);

  useEffect(() => { fetchSubscribers(); fetchCampaigns(); fetchGroups(); }, [fetchSubscribers, fetchCampaigns, fetchGroups]);
  useEffect(() => { fetchGroupPreview(selectedGroupId); }, [selectedGroupId, fetchGroupPreview]);

  const openSendDialog = () => {
    setSendDialogGroupId(selectedGroupId);
    setSendDialogSchedule('');
    setShowSendDialog(true);
  };

  const handleConfirmedSend = async () => {
    setShowSendDialog(false);
    setSending(true); setSendResult(null); setSendError(null);
    try {
      const r = await axios.post(`${NEWSLETTER_API}/send`, {
        subject, htmlBody,
        group_id: sendDialogGroupId || undefined,
        senderName: senderName.trim() || undefined,
      }, { headers: authHeader, timeout: 120_000 });
      setSendResult(r.data); fetchCampaigns();
    } catch (err: any) { setSendError(err.response?.data?.error ?? err.message); }
    finally { setSending(false); }
  };

  const handlePreviewSend = async () => {
    if (!subject.trim() || !htmlBody.trim()) return;
    setSendingPreview(true); setPreviewResult(null);
    try {
      const extras = previewExtraEmails.split(',').map(e => e.trim().toLowerCase()).filter(e => e && e.includes('@'));
      const r = await axios.post(`${NEWSLETTER_API}/preview`, { subject, htmlBody, previewEmails: extras.length > 0 ? extras : undefined, senderName: senderName.trim() || undefined }, { headers: authHeader, timeout: 60_000 });
      setPreviewResult(r.data);
    } catch (err: any) { setPreviewResult({ sent: 0, failed: 1, errors: [err.response?.data?.error ?? err.message] }); }
    finally { setSendingPreview(false); }
  };

  const handleSaveDraft = async () => {
    setSavingDraft(true); setDraftSaved(false);
    try {
      const payload = {
        subject, html_body: htmlBody, sender_name: senderName,
        group_id: selectedGroupId || null,
        scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : null,
      };
      if (editingDraftId) {
        await axios.patch(`${NEWSLETTER_API}/drafts/${editingDraftId}`, payload, { headers: authHeader });
      } else {
        const r = await axios.post(`${NEWSLETTER_API}/drafts`, payload, { headers: authHeader });
        setEditingDraftId(r.data.id);
      }
      setDraftSaved(true);
      setTimeout(() => setDraftSaved(false), 3000);
    } catch { /* ignore */ } finally { setSavingDraft(false); }
  };

  const previewHtml = `${htmlBody}<br/><br/><hr style="border:none;border-top:1px solid #eee;margin:24px 0"/><p style="font-size:12px;color:#999;text-align:center;">You're receiving this because you signed up for Famēlia updates.<br/><a href="#" style="color:#999;">Unsubscribe</a></p>`;

  const iframeDoc = (() => {
    const html = previewHtml.replace(/\{\{firstName\}\}/g, 'there');
    const inject = `<meta name="color-scheme" content="only light"><style>:root,html,body{color-scheme:only light!important;forced-color-adjust:none!important}</style>`;
    if (/<head[^>]*>/i.test(html)) return html.replace(/(<head[^>]*>)/i, `$1${inject}`);
    return inject + html;
  })();

  const sendDialogGroupCount = (() => {
    if (!sendDialogGroupId) return subscribers.length;
    // fetch count lazily; use cached groupPreviewCount if same group
    if (sendDialogGroupId === selectedGroupId && groupPreviewCount !== null) return groupPreviewCount;
    return null;
  })();

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5" fontWeight={700}>Compose Newsletter</Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <PeopleIcon sx={{ color: '#FF8C00' }} />
          {loadingSubscribers ? <CircularProgress size={18} /> : subscriberError ? <Chip label="Error" color="error" size="small" /> : (
            <Chip label={`${subscribers.length} subscriber${subscribers.length !== 1 ? 's' : ''}`} sx={{ bgcolor: '#FFF3E0', color: '#E65100', fontWeight: 700, fontSize: '0.85rem' }} />
          )}
          <Button size="small" variant="text" onClick={fetchSubscribers} disabled={loadingSubscribers} sx={{ textTransform: 'none', color: '#FF8C00' }}>Refresh</Button>
        </Stack>
      </Stack>

      {subscriberError && <Alert severity="error" sx={{ mb: 2 }}>{subscriberError}</Alert>}
      {editingDraftId && (
        <Alert severity="info" sx={{ mb: 2 }} icon={<DraftsIcon fontSize="inherit" />}
          action={<Button color="inherit" size="small" onClick={handleClearEditingDraft} sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>New Draft</Button>}>
          <strong>Editing saved draft</strong> — changes will update the existing draft when you save.
        </Alert>
      )}
      {sendResult && <Alert severity={sendResult.failed === 0 ? 'success' : 'warning'} sx={{ mb: 2 }} onClose={() => setSendResult(null)}><strong>Sent {sendResult.sent}</strong> email{sendResult.sent !== 1 ? 's' : ''}.{sendResult.failed > 0 && <> {sendResult.failed} failed.</>}</Alert>}
      {sendError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSendError(null)}>{sendError}</Alert>}
      {draftSaved && <Alert severity="success" sx={{ mb: 2 }}>Newsletter saved{scheduledAt ? ` — scheduled for ${new Date(scheduledAt).toLocaleString()}` : ' as draft'}!</Alert>}

      <Stack spacing={2.5}>
        {/* Subject + Sender */}
        <Stack direction="row" spacing={2}>
          <Box flex={2}>
            <Typography variant="subtitle2" fontWeight={700} mb={0.5} color="text.secondary">Subject</Typography>
            <TextField fullWidth placeholder="e.g. Famēlia — August 2026 Update" value={subject} onChange={e => setSubject(e.target.value)} variant="outlined" size="small" inputProps={{ maxLength: 200 }} />
          </Box>
          <Box flex={1}>
            <Typography variant="subtitle2" fontWeight={700} mb={0.5} color="text.secondary">Sender Name</Typography>
            <TextField fullWidth placeholder="e.g. Famēlia" value={senderName} onChange={e => setSenderName(e.target.value)} variant="outlined" size="small" inputProps={{ maxLength: 80 }} helperText={`"${senderName || 'Famēlia'}" in inbox`} />
          </Box>
        </Stack>

        {/* Group selector */}
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={groups.length > 0 ? 1 : 0}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <GroupIcon sx={{ color: '#FF8C00', fontSize: 18 }} />
              <Typography variant="subtitle2" fontWeight={700}>Send to Group</Typography>
            </Stack>
            <Button size="small" startIcon={<AddIcon />} onClick={() => { setEditingGroup(null); setNewGroupName(''); setNewGroupDesc(''); setNewGroupExcludes(''); setNewGroupIncludes(''); setShowGroupManager(v => !v); }}
              sx={{ textTransform: 'none', color: '#FF8C00', fontWeight: 600, fontSize: '0.78rem' }}>{showGroupManager ? 'Done' : 'Manage Groups'}</Button>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={2}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <Select value={selectedGroupId} onChange={e => setSelectedGroupId(e.target.value)} displayEmpty>
                <MenuItem value=""><em>All Subscribers ({subscribers.length})</em></MenuItem>
                {groups.map(g => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
              </Select>
            </FormControl>
            {selectedGroupId && groupPreviewCount !== null && <Chip label={`${groupPreviewCount} recipient${groupPreviewCount !== 1 ? 's' : ''}`} size="small" sx={{ bgcolor: '#FFF3E0', color: '#E65100', fontWeight: 700 }} />}
          </Stack>
          {showGroupManager && (
            <Box mt={2} pt={2} borderTop="1px solid #eee">
              <Stack spacing={1} mb={2}>
                {groups.map(g => (
                  <Box key={g.id}>
                    {editingGroup?.id === g.id ? (
                      <Stack spacing={1}>
                        <TextField size="small" label="Name" value={newGroupName} onChange={e => setNewGroupName(e.target.value)} fullWidth />
                        <TextField size="small" label="Description" value={newGroupDesc} onChange={e => setNewGroupDesc(e.target.value)} fullWidth />
                        <TextField size="small" label="Exclude patterns" value={newGroupExcludes} onChange={e => setNewGroupExcludes(e.target.value)} fullWidth helperText="Comma-separated" />
                        <TextField size="small" label="Include ONLY emails" value={newGroupIncludes} onChange={e => setNewGroupIncludes(e.target.value)} fullWidth helperText="Leave blank to use excludes" />
                        <Stack direction="row" spacing={1}>
                          <Button size="small" variant="contained" disabled={savingGroup} onClick={async () => { setSavingGroup(true); try { await axios.patch(`${NEWSLETTER_API}/groups/${g.id}`, { name: newGroupName, description: newGroupDesc, exclude_patterns: newGroupExcludes.split(',').map(s => s.trim()).filter(Boolean), include_only_emails: newGroupIncludes.split(',').map(s => s.trim().toLowerCase()).filter(Boolean) }, { headers: authHeader }); setEditingGroup(null); await fetchGroups(); } finally { setSavingGroup(false); } }} sx={{ borderRadius: '16px', textTransform: 'none', bgcolor: '#FF8C00' }}>Save</Button>
                          <Button size="small" onClick={() => setEditingGroup(null)} sx={{ borderRadius: '16px', textTransform: 'none' }}>Cancel</Button>
                        </Stack>
                      </Stack>
                    ) : (
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 0.5 }}>
                        <Box>
                          <Typography variant="body2" fontWeight={700}>{g.name}</Typography>
                          {g.description && <Typography variant="caption" color="text.secondary">{g.description}</Typography>}
                        </Box>
                        <Stack direction="row">
                          <Tooltip title="Edit"><IconButton size="small" onClick={() => { setEditingGroup(g); setNewGroupName(g.name); setNewGroupDesc(g.description); setNewGroupExcludes(g.exclude_patterns.join(', ')); setNewGroupIncludes(g.include_only_emails.join(', ')); }}><EditIcon fontSize="small" sx={{ color: '#FF8C00' }} /></IconButton></Tooltip>
                          <Tooltip title="Delete"><IconButton size="small" onClick={async () => { if (!window.confirm(`Delete group "${g.name}"?`)) return; await axios.delete(`${NEWSLETTER_API}/groups/${g.id}`, { headers: authHeader }); await fetchGroups(); }}><DeleteIcon fontSize="small" sx={{ color: '#D32F2F' }} /></IconButton></Tooltip>
                        </Stack>
                      </Stack>
                    )}
                  </Box>
                ))}
              </Stack>
              {!editingGroup && (
                <Box pt={1.5} borderTop="1px solid #eee">
                  <Typography variant="caption" fontWeight={700} color="text.secondary" mb={1} display="block">NEW GROUP</Typography>
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1}>
                      <TextField size="small" label="Group name" value={newGroupName} onChange={e => setNewGroupName(e.target.value)} sx={{ flex: 1 }} />
                      <TextField size="small" label="Description" value={newGroupDesc} onChange={e => setNewGroupDesc(e.target.value)} sx={{ flex: 1 }} />
                    </Stack>
                    <TextField size="small" label="Exclude patterns" value={newGroupExcludes} onChange={e => setNewGroupExcludes(e.target.value)} fullWidth />
                    <TextField size="small" label="Include ONLY emails" value={newGroupIncludes} onChange={e => setNewGroupIncludes(e.target.value)} fullWidth />
                    <Button size="small" variant="outlined" disabled={!newGroupName.trim() || savingGroup} onClick={async () => { setSavingGroup(true); try { await axios.post(`${NEWSLETTER_API}/groups`, { name: newGroupName.trim(), description: newGroupDesc.trim(), exclude_patterns: newGroupExcludes.split(',').map(s => s.trim()).filter(Boolean), include_only_emails: newGroupIncludes.split(',').map(s => s.trim().toLowerCase()).filter(Boolean) }, { headers: authHeader }); setNewGroupName(''); setNewGroupDesc(''); setNewGroupExcludes(''); setNewGroupIncludes(''); await fetchGroups(); } finally { setSavingGroup(false); } }} sx={{ alignSelf: 'flex-start', borderRadius: '16px', textTransform: 'none', borderColor: '#FF8C00', color: '#FF8C00' }}>
                      <AddIcon fontSize="small" sx={{ mr: 0.5 }} /> Create Group
                    </Button>
                  </Stack>
                </Box>
              )}
            </Box>
          )}
        </Paper>

        {/* Email Body Editor — collapsible */}
        <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="subtitle2" fontWeight={700} color="text.secondary">Email Body</Typography>
            <Button size="small" onClick={() => setEditorCollapsed(v => !v)}
              sx={{ textTransform: 'none', color: '#888', fontSize: '0.78rem' }}>
              {editorCollapsed ? '▼ Expand Editor' : '▲ Collapse Editor'}
            </Button>
          </Stack>
          <Box sx={{ display: editorCollapsed ? 'none' : 'block' }}>
            <EmailEditor value={htmlBody} onChange={setHtmlBody} editorMode={editorMode} setEditorMode={setEditorMode} />
          </Box>
          {editorCollapsed && htmlBody.trim() && (
            <Paper variant="outlined" onClick={() => setEditorCollapsed(false)} sx={{ p: 1.5, borderRadius: 2, cursor: 'pointer', bgcolor: '#FAFAFA', '&:hover': { bgcolor: '#F5F5F5' } }}>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#888', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {htmlBody.slice(0, 120)}…
              </Typography>
              <Typography variant="caption" color="primary" sx={{ fontSize: '0.7rem' }}>Click to expand editor</Typography>
            </Paper>
          )}
        </Box>

        {/* Action buttons */}
        <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="center">
          <Button variant="contained" size="large"
            startIcon={sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
            onClick={openSendDialog}
            disabled={sending || !subject.trim() || !htmlBody.trim() || subscribers.length === 0}
            sx={{ borderRadius: '20px', textTransform: 'none', fontWeight: 700, px: 3, bgcolor: '#FF8C00', '&:hover': { bgcolor: '#E07A00' } }}>
            {sending ? 'Sending…' : 'Send Newsletter'}
          </Button>
          <Button variant="outlined" size="large"
            startIcon={savingDraft ? <CircularProgress size={18} /> : <SaveIcon />}
            onClick={handleSaveDraft} disabled={savingDraft || (!subject.trim() && !htmlBody.trim())}
            sx={{ borderRadius: '20px', textTransform: 'none', fontWeight: 600, px: 3, borderColor: '#43A047', color: '#43A047', '&:hover': { borderColor: '#2E7D32', bgcolor: '#E8F5E9' } }}>
            {scheduledAt ? 'Save & Schedule' : 'Save Draft'}
          </Button>
          <Button variant="outlined" size="large"
            startIcon={<EmailIcon />}
            onClick={() => { setPreviewResult(null); setShowPreviewSendDialog(true); }} disabled={!subject.trim() || !htmlBody.trim()}
            sx={{ borderRadius: '20px', textTransform: 'none', fontWeight: 600, px: 3, borderColor: '#7B1FA2', color: '#7B1FA2', '&:hover': { borderColor: '#6A1B9A', bgcolor: '#F3E5F5' } }}>
            Send Test
          </Button>
        </Stack>

        {/* Prominent preview area */}
        {htmlBody.trim() && (
          <Box>
            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', border: '2px solid #FF8C00' }}>
              {/* Preview toolbar */}
              <Stack direction="row" alignItems="center" justifyContent="space-between" px={2} py={1}
                sx={{ bgcolor: '#FFF8F0', borderBottom: '1px solid #FFE0B2' }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PreviewIcon sx={{ color: '#FF8C00', fontSize: 20 }} />
                  <Typography variant="subtitle2" fontWeight={700} color="#E65100">Live Preview</Typography>
                  <Typography variant="caption" color="text.secondary">{'({{firstName}} → "there")'}</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <ToggleButtonGroup value={previewDevice} exclusive onChange={(_, v) => { if (v) setPreviewDevice(v); }} size="small">
                    <ToggleButton value="desktop" sx={{ textTransform: 'none', fontSize: '0.75rem', px: 1.5 }}>
                      🖥 Desktop
                    </ToggleButton>
                    <ToggleButton value="mobile" sx={{ textTransform: 'none', fontSize: '0.75rem', px: 1.5 }}>
                      📱 Mobile
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <Button size="small" onClick={() => setEditorCollapsed(v => !v)}
                    sx={{ textTransform: 'none', color: '#888', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                    {editorCollapsed ? '▼ Expand Editor' : '▲ Collapse Editor'}
                  </Button>
                </Stack>
              </Stack>
              {/* Email header bar */}
              <Stack direction="row" alignItems="center" spacing={2} px={3} py={1.5} sx={{ bgcolor: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary"><strong>From:</strong> {senderName || 'Famēlia'} &lt;hi@famelia.app&gt;</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary"><strong>Subject:</strong> {subject || '(no subject)'}</Typography>
                </Box>
              </Stack>
              {/* iframe preview */}
              <Box sx={{ display: 'flex', justifyContent: 'center', bgcolor: '#e9ecef', p: previewDevice === 'mobile' ? 3 : 0 }}>
                <Box sx={{
                  width: previewDevice === 'mobile' ? 375 : '100%',
                  boxShadow: previewDevice === 'mobile' ? '0 4px 24px rgba(0,0,0,0.18)' : 'none',
                  borderRadius: previewDevice === 'mobile' ? 3 : 0,
                  overflow: 'hidden',
                  transition: 'all 0.2s',
                }}>
                  <iframe
                    key={previewDevice}
                    title="Email Preview"
                    srcDoc={iframeDoc}
                    style={{ width: '100%', minHeight: previewDevice === 'mobile' ? 700 : 600, border: 'none', display: 'block', colorScheme: 'only light' } as React.CSSProperties}
                  />
                </Box>
              </Box>
            </Paper>
          </Box>
        )}
      </Stack>

      {/* Send confirmation dialog */}
      <Dialog open={showSendDialog} onClose={() => setShowSendDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Confirm — Send Newsletter</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} mt={0.5}>
            <Box sx={{ bgcolor: '#FFF8F0', border: '1px solid #FFE0B2', borderRadius: 2, p: 2 }}>
              <Typography variant="body2" color="text.secondary"><strong>Subject:</strong> {subject}</Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}><strong>From:</strong> {senderName || 'Famēlia'} &lt;hi@famelia.app&gt;</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" fontWeight={700} mb={1}>Send to</Typography>
              <FormControl fullWidth size="small">
                <Select value={sendDialogGroupId} onChange={e => setSendDialogGroupId(e.target.value)} displayEmpty>
                  <MenuItem value=""><em>All Subscribers ({subscribers.length})</em></MenuItem>
                  {groups.map(g => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
                </Select>
              </FormControl>
              {sendDialogGroupCount !== null && sendDialogGroupId && (
                <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
                  ~{sendDialogGroupCount} recipient{sendDialogGroupCount !== 1 ? 's' : ''}
                </Typography>
              )}
            </Box>
            <Box>
              <Typography variant="subtitle2" fontWeight={700} mb={1}>When</Typography>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Button variant={!sendDialogSchedule ? 'contained' : 'outlined'} size="small"
                  onClick={() => setSendDialogSchedule('')}
                  sx={{ borderRadius: '16px', textTransform: 'none', ...(sendDialogSchedule ? { borderColor: '#ccc', color: '#666' } : { bgcolor: '#FF8C00', '&:hover': { bgcolor: '#E07A00' } }) }}>
                  Send Immediately
                </Button>
                <TextField type="datetime-local" size="small" value={sendDialogSchedule}
                  onChange={e => setSendDialogSchedule(e.target.value)}
                  InputLabelProps={{ shrink: true }} label="Or schedule for"
                  sx={{ minWidth: 230 }} />
              </Stack>
              {sendDialogSchedule && (
                <Alert severity="info" sx={{ mt: 1.5, fontSize: '0.8rem' }}>
                  Will be saved as a scheduled draft — you can send it manually from the Saved Drafts tab at the scheduled time.
                </Alert>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setShowSendDialog(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          {sendDialogSchedule ? (
            <Button variant="contained" onClick={async () => {
              setShowSendDialog(false);
              setSavingDraft(true);
              try {
                const payload = { subject, html_body: htmlBody, sender_name: senderName, group_id: sendDialogGroupId || null, scheduled_at: new Date(sendDialogSchedule).toISOString() };
                if (editingDraftId) await axios.patch(`${NEWSLETTER_API}/drafts/${editingDraftId}`, payload, { headers: authHeader });
                else { const r = await axios.post(`${NEWSLETTER_API}/drafts`, payload, { headers: authHeader }); setEditingDraftId(r.data.id); }
                setDraftSaved(true); setTimeout(() => setDraftSaved(false), 3000);
              } catch { /* ignore */ } finally { setSavingDraft(false); }
            }} sx={{ textTransform: 'none', borderRadius: '16px', bgcolor: '#43A047', '&:hover': { bgcolor: '#2E7D32' } }}>
              <ScheduleIcon sx={{ mr: 0.5, fontSize: 18 }} /> Save & Schedule
            </Button>
          ) : (
            <Button variant="contained"
              startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              onClick={handleConfirmedSend}
              disabled={sending}
              sx={{ textTransform: 'none', borderRadius: '16px', bgcolor: '#FF8C00', '&:hover': { bgcolor: '#E07A00' }, fontWeight: 700 }}>
              Yes, Send Now
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Test send dialog */}
      <Dialog open={showPreviewSendDialog} onClose={() => setShowPreviewSendDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Send Test Email</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>A test email will always be sent to:</Typography>
          <Stack direction="row" spacing={1} mb={2} flexWrap="wrap">
            <Chip label="brandon@famelia.app" size="small" sx={{ bgcolor: '#E8F5E9', fontWeight: 600 }} />
            <Chip label="fangyangelina@gmail.com" size="small" sx={{ bgcolor: '#E8F5E9', fontWeight: 600 }} />
          </Stack>
          <TextField fullWidth size="small" label="Additional emails (comma-separated)" value={previewExtraEmails} onChange={e => setPreviewExtraEmails(e.target.value)} helperText="Optional" />
          {previewResult && <Alert severity={previewResult.failed === 0 ? 'success' : 'warning'} sx={{ mt: 2 }}><strong>Test sent to {previewResult.sent} recipient{previewResult.sent !== 1 ? 's' : ''}</strong></Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowPreviewSendDialog(false)} sx={{ textTransform: 'none' }}>Close</Button>
          <Button variant="contained" onClick={handlePreviewSend} disabled={sendingPreview || !subject.trim() || !htmlBody.trim()}
            startIcon={sendingPreview ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            sx={{ textTransform: 'none', borderRadius: '16px', bgcolor: '#7B1FA2', '&:hover': { bgcolor: '#6A1B9A' } }}>
            {sendingPreview ? 'Sending…' : 'Send Test'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Past campaigns */}
      <Divider sx={{ my: 4 }} />
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <MarkEmailReadIcon sx={{ color: '#FF8C00' }} />
          <Typography variant="h6" fontWeight={700}>Past Campaigns</Typography>
        </Stack>
        <Button size="small" variant="text" onClick={fetchCampaigns} disabled={loadingCampaigns} sx={{ textTransform: 'none', color: '#FF8C00' }}>Refresh</Button>
      </Stack>
      {loadingCampaigns ? <CircularProgress size={20} /> : campaigns.length === 0 ? <Typography variant="body2" color="text.secondary">No newsletters sent yet.</Typography> : (
        <Table size="small">
          <TableHead><TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Sent</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Recipients</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Opened</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Clicked</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {campaigns.map(c => {
              const openRate = c.recipients_count > 0 ? Math.round((c.opened_count / c.recipients_count) * 100) : 0;
              const clickRate = c.recipients_count > 0 ? Math.round((c.clicked_count / c.recipients_count) * 100) : 0;
              return (<TableRow key={c.id}>
                <TableCell sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.subject}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: '0.8rem' }}>{new Date(c.sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</TableCell>
                <TableCell>{c.recipients_count}</TableCell>
                <TableCell><Stack direction="row" alignItems="center" spacing={0.5}><span>{c.opened_count}</span>{c.recipients_count > 0 && <Chip label={`${openRate}%`} size="small" sx={{ bgcolor: openRate >= 30 ? '#E8F5E9' : '#FFF3E0', color: openRate >= 30 ? '#2E7D32' : '#E65100', fontSize: '0.7rem', height: 18 }} />}</Stack></TableCell>
                <TableCell><Stack direction="row" alignItems="center" spacing={0.5}><span>{c.clicked_count}</span>{c.recipients_count > 0 && <Chip label={`${clickRate}%`} size="small" sx={{ bgcolor: '#F3E5F5', color: '#7B1FA2', fontSize: '0.7rem', height: 18 }} />}</Stack></TableCell>
              </TableRow>);
            })}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}

// ─── SAVED DRAFTS SECTION ────────────────────────────────────────────────────
function DraftsSection({ authHeader, onEditDraft }: {
  authHeader: { Authorization: string };
  onEditDraft: (draft: Draft) => void;
}) {
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [loadingEditId, setLoadingEditId] = useState<string | null>(null);

  const fetchDrafts = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${NEWSLETTER_API}/drafts`, { headers: authHeader }); setDrafts(r.data.drafts ?? []); }
    catch { /* non-fatal */ } finally { setLoading(false); }
  }, [authHeader]);

  useEffect(() => { fetchDrafts(); }, [fetchDrafts]);

  const handleSendDraft = async (id: string) => {
    if (!window.confirm('Send this newsletter now to all subscribers?')) return;
    setSendingId(id);
    try { await axios.post(`${NEWSLETTER_API}/drafts/${id}/send`, {}, { headers: authHeader, timeout: 120_000 }); await fetchDrafts(); }
    catch (err: any) { alert(err.response?.data?.error ?? 'Failed to send.'); }
    finally { setSendingId(null); }
  };

  const handleEdit = async (id: string) => {
    setLoadingEditId(id);
    try {
      const r = await axios.get(`${NEWSLETTER_API}/drafts/${id}`, { headers: authHeader });
      onEditDraft(r.data as Draft);
    } catch { alert('Failed to load draft.'); }
    finally { setLoadingEditId(null); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this draft?')) return;
    try { await axios.delete(`${NEWSLETTER_API}/drafts/${id}`, { headers: authHeader }); await fetchDrafts(); } catch { /* ignore */ }
  };

  if (loading) return <Box textAlign="center" py={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={3}>Saved Newsletters</Typography>
      {drafts.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <DraftsIcon sx={{ fontSize: 48, color: '#CCC', mb: 1 }} />
          <Typography color="text.secondary">No saved newsletters yet. Use "Save Draft" or "Save & Schedule" from the Compose tab.</Typography>
        </Paper>
      ) : (
        <Table size="small">
          <TableHead><TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Scheduled</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Last Updated</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {drafts.map(d => (
              <TableRow key={d.id} hover sx={{ cursor: d.status !== 'sent' ? 'pointer' : 'default' }}
                onClick={() => { if (d.status !== 'sent') handleEdit(d.id); }}>
                <TableCell sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.subject || '(no subject)'}</TableCell>
                <TableCell>
                  <Chip label={d.status} size="small" sx={{
                    fontWeight: 600, fontSize: '0.75rem',
                    bgcolor: d.status === 'scheduled' ? '#E3F2FD' : d.status === 'sent' ? '#E8F5E9' : '#F5F5F5',
                    color: d.status === 'scheduled' ? '#1565C0' : d.status === 'sent' ? '#2E7D32' : '#666',
                  }} />
                </TableCell>
                <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                  {d.scheduled_at ? new Date(d.scheduled_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}
                </TableCell>
                <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                  {new Date(d.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} onClick={e => e.stopPropagation()}>
                    {d.status !== 'sent' && (
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(d.id)} disabled={loadingEditId === d.id}>
                          {loadingEditId === d.id ? <CircularProgress size={16} /> : <EditIcon fontSize="small" sx={{ color: '#1565C0' }} />}
                        </IconButton>
                      </Tooltip>
                    )}
                    {d.status !== 'sent' && (
                      <Tooltip title="Send Now">
                        <IconButton size="small" onClick={() => handleSendDraft(d.id)} disabled={sendingId === d.id}>
                          {sendingId === d.id ? <CircularProgress size={16} /> : <SendIcon fontSize="small" sx={{ color: '#FF8C00' }} />}
                        </IconButton>
                      </Tooltip>
                    )}
                    {d.status !== 'sent' && (
                      <Tooltip title="Delete"><IconButton size="small" onClick={() => handleDelete(d.id)}><DeleteIcon fontSize="small" sx={{ color: '#D32F2F' }} /></IconButton></Tooltip>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}

// ─── WELCOME EMAIL SECTION ───────────────────────────────────────────────────
function WelcomeEmailSection({ authHeader }: { authHeader: { Authorization: string } }) {
  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [senderName, setSenderName] = useState('Famēlia');
  const [isActive, setIsActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState<SendResult | null>(null);
  const [showPreview, setShowPreview] = useState(true);
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'mobile'>('desktop');

  const fetchTemplate = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${NEWSLETTER_API}/welcome-email`, { headers: authHeader });
      const t = r.data.template as WelcomeTemplate | null;
      if (t) { setSubject(t.subject); setHtmlBody(t.html_body); setSenderName(t.sender_name); setIsActive(t.is_active); }
    } catch { /* non-fatal */ } finally { setLoading(false); }
  }, [authHeader]);

  useEffect(() => { fetchTemplate(); }, [fetchTemplate]);

  const persistTemplate = async (active?: boolean) => {
    const body: Record<string, unknown> = {
      subject, html_body: htmlBody, sender_name: senderName,
    };
    if (typeof active === 'boolean') body.is_active = active;
    await axios.post(`${NEWSLETTER_API}/welcome-email`, body, { headers: authHeader });
  };

  const handleSave = async () => {
    setSaving(true); setSaved(false);
    try {
      await persistTemplate();
      setSaved(true); setTimeout(() => setSaved(false), 3000);
    } catch { /* ignore */ } finally { setSaving(false); }
  };

  const handleToggleActive = async () => {
    const next = !isActive;
    setIsActive(next);
    setSaving(true); setSaved(false);
    try {
      await persistTemplate(next);
      setSaved(true); setTimeout(() => setSaved(false), 3000);
    } catch {
      setIsActive(!next);
    } finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTestSending(true); setTestResult(null);
    try {
      const r = await axios.post(`${NEWSLETTER_API}/welcome-email/test`, { subject, html_body: htmlBody, sender_name: senderName }, { headers: authHeader, timeout: 30_000 });
      setTestResult(r.data);
    } catch (err: any) { setTestResult({ sent: 0, failed: 1, errors: [err.response?.data?.error ?? err.message] }); }
    finally { setTestSending(false); }
  };

  const iframeDoc = (() => {
    const html = htmlBody.replace(/\{\{firstName\}\}/g, 'there');
    const inject = `<meta name="color-scheme" content="only light"><style>:root,html,body{color-scheme:only light!important;forced-color-adjust:none!important}</style>`;
    if (/<head[^>]*>/i.test(html)) return html.replace(/(<head[^>]*>)/i, `$1${inject}`);
    return inject + html;
  })();

  if (loading) return <Box textAlign="center" py={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Welcome Email</Typography>
          <Typography variant="body2" color="text.secondary">
            When Active, new Google, Apple, or email signups get this email a few seconds after they accept Terms.
            Guest accounts are skipped. The Active chip saves sending on/off immediately. Save Template only updates the content — it will not turn sending off.
          </Typography>
        </Box>
        <Chip
          label={isActive ? 'Active' : 'Inactive'}
          onClick={handleToggleActive}
          disabled={saving}
          sx={{ fontWeight: 700, cursor: saving ? 'default' : 'pointer', bgcolor: isActive ? '#E8F5E9' : '#FFEBEE', color: isActive ? '#2E7D32' : '#C62828' }}
        />
      </Stack>

      {saved && <Alert severity="success" sx={{ mb: 2 }}>Welcome email template saved!</Alert>}
      {testResult && <Alert severity={testResult.failed === 0 ? 'success' : 'warning'} sx={{ mb: 2 }} onClose={() => setTestResult(null)}>Test sent to {testResult.sent} recipient{testResult.sent !== 1 ? 's' : ''}</Alert>}

      <Stack spacing={2.5}>
        <Box>
          <Typography variant="subtitle2" fontWeight={700} mb={0.5} color="text.secondary">Subject</Typography>
          <TextField fullWidth placeholder="e.g. Welcome to Famēlia, {{firstName}}!" value={subject} onChange={e => setSubject(e.target.value)} variant="outlined" size="small" inputProps={{ maxLength: 200 }} />
        </Box>
        <Box>
          <Typography variant="subtitle2" fontWeight={700} mb={0.5} color="text.secondary">Sender Name</Typography>
          <TextField fullWidth placeholder="e.g. Famēlia" value={senderName} onChange={e => setSenderName(e.target.value)} variant="outlined" size="small" inputProps={{ maxLength: 80 }} />
        </Box>

        <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="subtitle2" fontWeight={700} color="text.secondary">HTML</Typography>
            <Button size="small" startIcon={<UploadFileIcon />} component="label"
              sx={{ textTransform: 'none', color: '#555', fontWeight: 600, fontSize: '0.8rem' }}>
              Upload HTML
              <input type="file" accept=".html,.htm" hidden onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = (ev) => {
                  const c = ev.target?.result as string;
                  if (c) setHtmlBody(c);
                };
                reader.readAsText(file);
                e.target.value = '';
              }} />
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" mb={1} display="block">
            Paste the exact Canva / builder HTML. This is sent unchanged — use {'{{firstName}}'} only if you want personalization.
          </Typography>
          <TextField fullWidth multiline minRows={14} maxRows={36} value={htmlBody} onChange={e => setHtmlBody(e.target.value)} variant="outlined"
            placeholder={'<!DOCTYPE html>\n<html>\n<body>\n  …\n</body>\n</html>'}
            sx={{ '& .MuiOutlinedInput-root': { fontFamily: '"Fira Code", "SF Mono", monospace', fontSize: '13px', lineHeight: 1.5, bgcolor: '#FAFAFA', color: '#1E1E1E', borderRadius: 2 } }} />
        </Box>

        <Stack direction="row" spacing={1.5} flexWrap="wrap">
          <Button variant="contained" size="large" startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            onClick={handleSave} disabled={saving}
            sx={{ borderRadius: '20px', textTransform: 'none', fontWeight: 700, px: 3, bgcolor: '#43A047', '&:hover': { bgcolor: '#2E7D32' } }}>
            {saving ? 'Saving…' : 'Save Template'}
          </Button>
          <Button variant="outlined" size="large" startIcon={<PreviewIcon />}
            onClick={() => setShowPreview(v => !v)} disabled={!htmlBody.trim()}
            sx={{ borderRadius: '20px', textTransform: 'none', fontWeight: 600, px: 3, borderColor: '#FF8C00', color: '#E65100', '&:hover': { borderColor: '#E07A00', bgcolor: '#FFF8F0' } }}>
            {showPreview ? 'Hide Preview' : 'Preview'}
          </Button>
          <Button variant="outlined" size="large" startIcon={testSending ? <CircularProgress size={18} /> : <EmailIcon />}
            onClick={handleTest} disabled={testSending || !subject.trim() || !htmlBody.trim()}
            sx={{ borderRadius: '20px', textTransform: 'none', fontWeight: 600, px: 3, borderColor: '#7B1FA2', color: '#7B1FA2', '&:hover': { borderColor: '#6A1B9A', bgcolor: '#F3E5F5' } }}>
            Send Test
          </Button>
        </Stack>

        {showPreview && htmlBody.trim() && (
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', border: '2px solid #FF8C00' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" px={2} py={1}
              sx={{ bgcolor: '#FFF8F0', borderBottom: '1px solid #FFE0B2' }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <PreviewIcon sx={{ color: '#FF8C00', fontSize: 20 }} />
                <Typography variant="subtitle2" fontWeight={700} color="#E65100">Live Preview</Typography>
                <Typography variant="caption" color="text.secondary">{'({{firstName}} → "there")'}</Typography>
              </Stack>
              <ToggleButtonGroup value={previewDevice} exclusive onChange={(_, v) => { if (v) setPreviewDevice(v); }} size="small">
                <ToggleButton value="desktop" sx={{ textTransform: 'none', fontSize: '0.75rem', px: 1.5 }}>🖥 Desktop</ToggleButton>
                <ToggleButton value="mobile" sx={{ textTransform: 'none', fontSize: '0.75rem', px: 1.5 }}>📱 Mobile</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={2} px={3} py={1.5} sx={{ bgcolor: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
              <Typography variant="caption" color="text.secondary"><strong>From:</strong> {senderName || 'Famēlia'} &lt;hello@famelia.app&gt;</Typography>
              <Typography variant="caption" color="text.secondary"><strong>Subject:</strong> {subject || '(no subject)'}</Typography>
            </Stack>
            <Box sx={{ display: 'flex', justifyContent: 'center', bgcolor: '#e9ecef', p: previewDevice === 'mobile' ? 3 : 0 }}>
              <Box sx={{
                width: previewDevice === 'mobile' ? 375 : '100%',
                boxShadow: previewDevice === 'mobile' ? '0 4px 24px rgba(0,0,0,0.18)' : 'none',
                borderRadius: previewDevice === 'mobile' ? 3 : 0,
                overflow: 'hidden',
              }}>
                <iframe
                  key={previewDevice}
                  title="Welcome Email Preview"
                  srcDoc={iframeDoc}
                  style={{ width: '100%', minHeight: previewDevice === 'mobile' ? 700 : 600, border: 'none', display: 'block', colorScheme: 'only light' } as React.CSSProperties}
                />
              </Box>
            </Box>
          </Paper>
        )}
      </Stack>
    </Box>
  );
}
